import { AsyncSelect, LookupOption } from '@/components';
import { DEFAULT_PAGING, HomeDto, isActiveProjectStatus } from '@/core';
import { useGetHomesListQuery } from '@/redux';
import { RtkqSpin } from '@/redux/rtkq';
import { useHomeLookupSource, useProjectLookupSource } from '@/views';
import { Button, Divider, Row, Skeleton, Space } from 'antd';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CommunityHome = Pick<HomeDto, 'id' | 'projectId' | 'projectStatus'>;
type SelectionSource = 'project' | 'home';

interface Props {
  communityId: string;
  selectedProjectIds: string[] | undefined;
  onSelectedProjectIdsChange: (value: string[] | undefined) => void;
  selectedHomeIds: string[] | undefined;
  onSelectedHomeIdsChange: (value: string[] | undefined) => void;
}

function getLookupOptionValues(options: LookupOption[]) {
  return options.map((x) => x.value);
}

function areSameIds(first: string[] | undefined, second: string[] | undefined) {
  return (first ?? []).join(',') === (second ?? []).join(',');
}

function getProjectIdsByHomeIds(
  homeIds: string[] | undefined,
  homesMap: Map<string, CommunityHome>,
) {
  const projectIds: string[] = [];
  const activeProjectIds: string[] = [];

  homeIds?.forEach((homeId) => {
    const home = homesMap.get(homeId);
    if (!home?.projectId) {
      return;
    }

    if (!projectIds.includes(home.projectId)) {
      projectIds.push(home.projectId);
    }

    if (
      isActiveProjectStatus(home.projectStatus) &&
      !activeProjectIds.includes(home.projectId)
    ) {
      activeProjectIds.push(home.projectId);
    }
  });

  if (projectIds.length <= 1) {
    return projectIds;
  }

  if (activeProjectIds.length === 1) {
    return activeProjectIds;
  }

  return projectIds;
}

function useCommunityHomeMaps(communityHomes: CommunityHome[]) {
  const homesMap = useMemo(
    () => new Map(communityHomes.map((x) => [x.id, x])),
    [communityHomes],
  );

  return {
    homesMap,
  };
}

function useSelectAllOptions(
  projectOptions: LookupOption[],
  homeOptions: LookupOption[],
  selectedProjectIds: string[] | undefined,
  selectedHomeIds: string[] | undefined,
) {
  const projectOptionIds = getLookupOptionValues(projectOptions);
  const homeOptionIds = getLookupOptionValues(homeOptions);

  const isAllProjectsSelected =
    !isEmpty(projectOptionIds) &&
    selectedProjectIds?.length === projectOptionIds.length;
  const isAllHomesSelected =
    !isEmpty(homeOptionIds) && selectedHomeIds?.length === homeOptionIds.length;

  return {
    projectOptionIds,
    homeOptionIds,
    isAllProjectsSelected,
    isAllHomesSelected,
  };
}

function useProjectIdsChangeHandler(props: {
  onSelectedHomeIdsChange: (value: string[] | undefined) => void;
  onSelectedProjectIdsChange: (value: string[] | undefined) => void;
  onSelectionSourceChange: (value: SelectionSource | undefined) => void;
}) {
  const {
    onSelectedHomeIdsChange,
    onSelectedProjectIdsChange,
    onSelectionSourceChange,
  } = props;

  return useCallback(
    (value: string[] | undefined) => {
      onSelectionSourceChange(isEmpty(value) ? undefined : 'project');
      onSelectedProjectIdsChange(value);

      if (isEmpty(value)) {
        onSelectedHomeIdsChange([]);
      }
    },
    [onSelectedHomeIdsChange, onSelectedProjectIdsChange, onSelectionSourceChange],
  );
}

function useHomeIdsChangeHandler(props: {
  onSelectedHomeIdsChange: (value: string[] | undefined) => void;
  onSelectionSourceChange: (value: SelectionSource | undefined) => void;
}) {
  const { onSelectedHomeIdsChange, onSelectionSourceChange } = props;

  return useCallback(
    (value: string[] | undefined) => {
      onSelectionSourceChange(isEmpty(value) ? undefined : 'home');
      onSelectedHomeIdsChange(value);
    },
    [onSelectedHomeIdsChange, onSelectionSourceChange],
  );
}

export function PricingSpecialRequestListFilterPanel(props: Props) {
  const {
    communityId,
    selectedProjectIds,
    onSelectedProjectIdsChange,
    selectedHomeIds,
    onSelectedHomeIdsChange,
  } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'pricing.specialRequest',
  });

  const [projectOptions, setProjectOptions] = useState<LookupOption[]>([]);
  const [homeOptions, setHomeOptions] = useState<LookupOption[]>([]);
  const [isHomeOptionsLoaded, setIsHomeOptionsLoaded] = useState(true);
  const [selectionSource, setSelectionSource] = useState<
    SelectionSource | undefined
  >();

  const projectDataSource = useProjectLookupSource({
    communityIds: [communityId],
  });
  const homeDataSource = useHomeLookupSource(communityId, selectedProjectIds);

  const { data: communityHomesData } = useGetHomesListQuery({
    communityId,
    paging: DEFAULT_PAGING,
    [RtkqSpin]: false,
  });

  const communityHomes = communityHomesData?.items ?? [];
  const { homesMap } = useCommunityHomeMaps(communityHomes);
  const {
    projectOptionIds,
    homeOptionIds,
    isAllProjectsSelected,
    isAllHomesSelected,
  } = useSelectAllOptions(
    projectOptions,
    homeOptions,
    selectedProjectIds,
    selectedHomeIds,
  );

  useEffect(() => {
    setHomeOptions([]);
    setIsHomeOptionsLoaded(false);
  }, [selectedProjectIds]);

  const handleHomeOptionsLoad = useCallback((options: LookupOption[]) => {
    setHomeOptions(options);
    setIsHomeOptionsLoaded(true);
  }, []);

  useEffect(() => {
    if (selectionSource !== 'project' || isEmpty(selectedProjectIds)) {
      return;
    }

    if (isEmpty(homeOptionIds)) {
      return;
    }

    if (!areSameIds(homeOptionIds, selectedHomeIds)) {
      onSelectedHomeIdsChange(homeOptionIds);
    }
  }, [
    homeOptionIds,
    onSelectedHomeIdsChange,
    selectedHomeIds,
    selectedProjectIds,
    selectionSource,
  ]);

  useEffect(() => {
    if (selectionSource !== 'home' || isEmpty(selectedHomeIds)) {
      return;
    }

    const nextProjectIds = getProjectIdsByHomeIds(selectedHomeIds, homesMap) ?? [];

    if (!isEmpty(nextProjectIds) && !areSameIds(nextProjectIds, selectedProjectIds)) {
      onSelectedProjectIdsChange(nextProjectIds);
    }
  }, [
    homesMap,
    onSelectedProjectIdsChange,
    selectedHomeIds,
    selectedProjectIds,
    selectionSource,
  ]);

  const handleProjectIdsChange = useProjectIdsChangeHandler({
    onSelectedHomeIdsChange,
    onSelectedProjectIdsChange,
    onSelectionSourceChange: setSelectionSource,
  });

  const handleHomeIdsChange = useHomeIdsChangeHandler({
    onSelectedHomeIdsChange,
    onSelectionSourceChange: setSelectionSource,
  });

  const isProjectHomePrefillLoading =
    !isEmpty(selectedHomeIds) &&
    !isHomeOptionsLoaded;

  return (
    <Space direction="vertical" style={{ width: '25%' }} size="large">
      <AsyncSelect
        value={selectedProjectIds as any}
        onChange={handleProjectIdsChange as any}
        preload
        dataSource={projectDataSource}
        placeholder={t('project')}
        mode="multiple"
        onSearchLoad={setProjectOptions}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: '2px' }} />
            <Row justify="center" className="p-2">
              <Button
                type="link"
                onClick={() =>
                  handleProjectIdsChange(
                    isAllProjectsSelected ? [] : projectOptionIds,
                  )
                }
              >
                {t(`${isAllProjectsSelected ? 'removeAll' : 'selectAll'}`)}
              </Button>
            </Row>
          </>
        )}
      />
      {isProjectHomePrefillLoading ? (
        <Skeleton.Input active block style={{ width: '100%' }} />
      ) : (
        <AsyncSelect
          key={selectedProjectIds?.toString()}
          value={selectedHomeIds as any}
          preload
          onChange={handleHomeIdsChange as any}
          dataSource={homeDataSource}
          placeholder={t('home')}
          mode="multiple"
          onSearchLoad={handleHomeOptionsLoad}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '2px' }} />
              <Row justify="center" className="p-2">
                <Button
                  type="link"
                  onClick={() =>
                    handleHomeIdsChange(isAllHomesSelected ? [] : homeOptionIds)
                  }
                >
                  {t(`${isAllHomesSelected ? 'removeAll' : 'selectAll'}`)}
                </Button>
              </Row>
            </>
          )}
        />
      )}
    </Space>
  );
}
